import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import {
  SettingsMenuItem,
  type SettingsMenuIcon,
} from '@/features/settings/settings-menu';

import {
  MemberStatusEnum,
  TeamRoleDisplayLabel,
  TeamTabTitle,
  visibleTeamTabs,
  type TeamDashboardData,
  type TeamTab,
} from '../team.types';

const TEAM_TAB_ICONS: Record<TeamTab, SettingsMenuIcon> = {
  members: Icons.attendees,
  analytics: Icons.insights,
  'data-controls': Icons.dataControls,
  billing: Icons.creditCard,
};

interface TeamSidebarProps {
  dashboard: TeamDashboardData;
  activeTab: TeamTab;
  isAdmin: boolean;
  onTabChange: (tab: TeamTab) => void;
  onBack: () => void;
}

export function TeamSidebar({
  dashboard,
  activeTab,
  isAdmin,
  onTabChange,
  onBack,
}: TeamSidebarProps) {
  const { team, members } = dashboard;
  const activeMemberCount = members.filter(
    (member) => member.status === MemberStatusEnum.enum.active
  ).length;

  return (
    <YStack className="h-full w-full min-h-0 gap-2">
      <SidebarRow.Button
        muted
        leading={<SidebarRow.Icon icon={Icons.chevronLeft} />}
        onClick={onBack}
      >
        Settings
      </SidebarRow.Button>

      <XStack className="items-center gap-2.5 px-[var(--row-pad)] py-2">
        <YStack center className="size-8 shrink-0 rounded-lg bg-accent">
          <span className="text-sm font-semibold text-accent-foreground">
            {team.name.charAt(0)}
          </span>
        </YStack>
        <YStack className="min-w-0">
          <span className="truncate text-sm font-medium text-foreground">
            {team.name}
          </span>
          <span className="truncate text-xs text-foreground-muted">
            {activeMemberCount} {activeMemberCount === 1 ? 'member' : 'members'}{' '}
            · {TeamRoleDisplayLabel[team.role]}
          </span>
        </YStack>
      </XStack>

      <nav aria-label="Team" className="py-2">
        <YStack className="w-full">
          {visibleTeamTabs(isAdmin).map((tab) => (
            <SettingsMenuItem
              key={tab}
              title={TeamTabTitle[tab]}
              icon={TEAM_TAB_ICONS[tab]}
              active={activeTab === tab}
              current={activeTab === tab}
              onClick={() => onTabChange(tab)}
            />
          ))}
        </YStack>
      </nav>
    </YStack>
  );
}
